<template>
  <div :class="$vuetify.theme.dark ? 'darkBackground' : 'lightBackground'">
    <!-- Header -->
    <div class="fill-width align-self-start">
      <v-card
        class="d-flex align-center fill-width"
        :color="$vuetify.theme.dark ? '#22272e' : 'white'"
        flat
        height="50"
        tile
      >
        <a class="pa-5" href="https://testapp.io">
          <img
            :src="
              $vuetify.theme.dark
                ? '/media/svg/logo-for-dark-theme.svg'
                : '/media/logos/logo_dark.svg'
            "
            alt="TestApp.io"
            height="30"
          />
        </a>
      </v-card>
    </div>

    <v-container>
      <!-- Loading State -->
      <v-row v-if="loading" justify="center">
        <v-col cols="12" md="8">
          <v-skeleton-loader
            type="card-heading, list-item-three-line, actions"
          ></v-skeleton-loader>
        </v-col>
      </v-row>

      <!-- Error State -->
      <v-row v-else-if="errorMessage" class="mb-15" justify="center">
        <v-col cols="12" md="8">
          <v-card class="pa-10">
            <v-col class="text-center titleFont bold-text" cols="12">
              Oops!
            </v-col>
            <v-col cols="12">
              <v-alert color="red" label outlined>
                <span v-html="errorMessage"></span>
              </v-alert>
            </v-col>
            <v-col class="text-center" cols="12">
              <img
                class="mx-auto my-auto"
                src="/media/error/not_available.png"
                width="260"
              />
            </v-col>
          </v-card>
        </v-col>
      </v-row>

      <!-- Install Page -->
      <v-row v-else class="mb-15" justify="center">
        <v-col cols="12" md="8">
          <v-card class="px-8 py-6" flat>
            <!-- App Info -->
            <v-row>
              <v-col cols="12" class="text-center">
                <v-avatar size="100" rounded>
                  <img
                    v-if="release.app.image"
                    :src="release.app.image"
                    :alt="release.app.name"
                  />
                  <v-icon v-else size="80">mdi-application</v-icon>
                </v-avatar>
              </v-col>
              <v-col cols="12" class="text-center">
                <h2 class="font-weight-bold">{{ release.app.name }}</h2>
                <p class="text--secondary">
                  Version {{ release.info.version }}
                </p>
              </v-col>
            </v-row>

            <!-- User Info -->
            <v-row v-if="release.user" class="mt-2">
              <v-col cols="12" class="d-flex align-center justify-center">
                <user-avatar :user-detail="release.user" />
                <div class="ml-2 d-flex flex-column">
                  <span class="text-body-2 font-weight-medium">
                    {{ release.user.name }}
                  </span>
                  <span class="text-caption text--secondary">
                    <vue-hoverable-date :date="release.created_at" />
                  </span>
                </div>
              </v-col>
            </v-row>

            <!-- Browser Detection Warning -->
            <v-row v-if="!isIOSSafari" class="mt-4">
              <v-col cols="12">
                <v-alert color="warning" outlined>
                  <v-icon left>mdi-alert</v-icon>
                  <div>
                    We're opening your default browser. If it's not Safari,
                    please copy this link and open it in Safari to install the
                    app.
                  </div>
                </v-alert>
              </v-col>
              <v-col cols="12" class="text-center">
                <copy-link :link="currentUrl" />
              </v-col>

              <!-- Desktop Mode Instructions for iPad/iPhone -->
              <v-col v-if="isPotentiallyIPadDesktopMode" cols="12" class="mt-4">
                <v-card outlined>
                  <v-card-text>
                    <h3 class="mb-3">
                      If you're using an iPad or iPhone, you may need to switch
                      to mobile mode:
                    </h3>
                    <v-img
                      width="100%"
                      contain
                      src="/media/instruction/enable_mobile_mode.jpg"
                      alt="Enable mobile mode in Safari"
                    ></v-img>
                    <div class="text-center mt-3">
                      <a
                        href="https://help.testapp.io/faq/enable-mobile-mode-on-safari/"
                        target="_blank"
                        class="primary--text font-weight-bold"
                      >
                        Click here for more information
                      </a>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Install Button -->
            <v-row v-else class="mt-4">
              <v-col cols="12" class="text-center">
                <v-btn
                  color="primary"
                  x-large
                  block
                  :loading="installing"
                  @click="installApp"
                >
                  <v-icon left>mdi-download</v-icon>
                  Install
                </v-btn>
              </v-col>
            </v-row>

            <!-- Installation Instructions -->
            <v-row v-if="isIOSSafari && showInstructions" class="mt-6">
              <v-col cols="12">
                <v-alert color="info" outlined>
                  <div class="font-weight-bold mb-2">Installation Started</div>
                  <p class="mb-2">
                    After installing, you'll find the app on your home screen.
                    Return to TestApp.io to share your feedback!
                  </p>
                </v-alert>
              </v-col>
            </v-row>

            <!-- Release Notes -->
            <v-row v-if="release.note" class="mt-6">
              <v-col cols="12">
                <v-divider class="mb-4"></v-divider>
                <div class="font-size-h2 mb-3 bold-text">Release Notes</div>
                <div style="white-space: pre-line">
                  <div v-if="release.note.length > 600">
                    {{
                      readMore ? release.note.substring(0, 600) : release.note
                    }}
                    <span
                      style="cursor: pointer"
                      class="primary--text font-weight-bold"
                      @click="readMore = !readMore"
                      >{{ readMore ? "... Read More" : "Read Less" }}</span
                    >
                  </div>
                  <div v-else>
                    {{ release.note }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Release Info -->
            <v-row class="mt-6">
              <v-col cols="12">
                <v-divider class="mb-4"></v-divider>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text--secondary text-caption mb-1">Version</div>
                <div class="font-weight-medium">
                  {{ release.info.version }}
                </div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text--secondary text-caption mb-1">Size</div>
                <div class="font-weight-medium">
                  {{ formatBytes(release.info.size) }}
                </div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text--secondary text-caption mb-1">Platform</div>
                <div class="font-weight-medium text-capitalize">
                  <v-icon
                    small
                    :color="release.platform === 'ios' ? 'grey' : 'green'"
                    v-text="
                      release.platform === 'android'
                        ? 'mdi-android'
                        : 'mdi-apple'
                    "
                  ></v-icon>
                  {{ release.platform }}
                </div>
              </v-col>
              <v-col v-if="release.info.signing_type" cols="6" sm="4">
                <div class="text--secondary text-caption mb-1">
                  Signing Type
                </div>
                <div class="font-weight-medium text-capitalize">
                  {{ release.info.signing_type }}
                </div>
              </v-col>
              <v-col v-if="release.info.package" cols="12" sm="8">
                <div class="text--secondary text-caption mb-1">Package</div>
                <div class="font-weight-medium text-break">
                  {{ release.info.package }}
                </div>
              </v-col>
            </v-row>

            <!-- Security Info Banner -->
            <v-row class="mt-6">
              <v-col cols="12">
                <v-alert
                  color="info"
                  outlined
                  dense
                  class="mb-0"
                  icon="mdi-shield-check"
                >
                  <div class="text-caption">
                    <strong>🔒 Secure Install Link</strong>
                    <br />
                    This page is secure and not publicly shared. This link
                    expires in 24 hours and can be regenerated from the
                    TestApp.io mobile app if needed.
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { formatBytes } from "@/core/services/helper.service";
import ApiService from "@/core/services/api.service";
import { SET_AUTH } from "@/store/users/auth.module";
import CopyLink from "@/view/components/Common/CopyLink.vue";
import UserAvatar from "@/view/components/Common/UserAvatar.vue";

export default {
  name: "InstallRelease",
  components: {
    CopyLink,
    UserAvatar,
  },
  metaInfo() {
    return {
      title: "Install Release - TestApp.io",
      meta: [
        // Prevent search engine indexing
        { name: "robots", content: "noindex, nofollow" },
        { name: "googlebot", content: "noindex, nofollow" },
        // Prevent caching
        {
          "http-equiv": "cache-control",
          content: "no-cache, no-store, must-revalidate",
        },
        { "http-equiv": "pragma", content: "no-cache" },
        { "http-equiv": "expires", content: "0" },
      ],
    };
  },
  data() {
    return {
      loading: true,
      errorMessage: null,
      release: null,
      installing: false,
      showInstructions: false,
      readMore: true,
      formatBytes,
    };
  },
  computed: {
    isIOSSafari() {
      const ua = navigator.userAgent;
      // Check for iOS devices (iPhone, iPad, iPod)
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      // Check for iPad in desktop mode (MacIntel + touch support)
      const isIPadDesktopMode =
        navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
      // Check if Safari (not Chrome, Firefox, Opera, etc.)
      const isSafari =
        /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
      // Return true if (iOS OR iPad desktop mode) AND Safari
      return (isIOS || isIPadDesktopMode) && isSafari;
    },
    isPotentiallyIPadDesktopMode() {
      // Check if this might be an iPad in desktop mode
      // MacIntel platform + touch support = likely iPad with "Request Desktop Website"
      return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    },
    currentUrl() {
      return window.location.href;
    },
  },
  mounted() {
    // Apply dark theme if user has dark mode enabled
    const darkTheme =
      this.$route.query.dark === "true" ||
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (darkTheme) {
      this.$vuetify.theme.dark = true;
    }

    this.validateTokenAndLoadRelease();
  },
  methods: {
    async validateTokenAndLoadRelease() {
      try {
        const token = this.$route.params.token;

        // Check if we already have a session for this token
        const sessionToken = localStorage.getItem("install_session_token");
        const sessionRelease = localStorage.getItem("install_session_release");

        if (sessionToken === token && sessionRelease) {
          // We already validated this token, use cached data
          try {
            this.release = JSON.parse(sessionRelease);
            this.loading = false;
            return;
          } catch (e) {
            // If parsing fails, clear cache and continue to validate
            localStorage.removeItem("install_session_token");
            localStorage.removeItem("install_session_release");
          }
        }

        // Call backend to validate token and get auth tokens + release data
        const response = await ApiService.post(
          "/portal/install/app/release/auth",
          {
            token,
          }
        );

        if (response.data.access_token && response.data.refresh_token) {
          // Auto-login user with correct token structure
          this.$store.commit(SET_AUTH, {
            token: response.data.access_token,
            refresh_token: response.data.refresh_token,
          });
        }

        // Set release data
        this.release = response.data.release;

        // Store session in localStorage for future use
        localStorage.setItem("install_session_token", token);
        localStorage.setItem(
          "install_session_release",
          JSON.stringify(response.data.release)
        );

        this.loading = false;
      } catch (error) {
        this.loading = false;

        // Clear cached session on error
        localStorage.removeItem("install_session_token");
        localStorage.removeItem("install_session_release");

        // Check for specific error codes
        const statusCode = error.response?.status;
        const serverMessage = error.response?.data?.message;

        // Check if it's a rate limit error (message contains "too many times")
        if (
          serverMessage &&
          serverMessage.toLowerCase().includes("too many times")
        ) {
          this.errorMessage =
            serverMessage ||
            "This install link has been used too many times.<br><br>" +
              "<strong>To install this release:</strong><br>" +
              "1. Open the TestApp.io mobile app<br>" +
              "2. Navigate to the release<br>" +
              "3. Tap 'Install' to generate a new secure link";
        } else if (statusCode === 401 || statusCode === 403) {
          // Unauthorized or forbidden - likely expired/invalid token
          this.errorMessage =
            serverMessage ||
            "This install link has expired or is invalid.<br><br>" +
              "<strong>To install this release:</strong><br>" +
              "1. Open the TestApp.io mobile app<br>" +
              "2. Navigate to the release<br>" +
              "3. Tap 'Install' to generate a new secure link";
        } else if (statusCode === 404) {
          // Release not found
          this.errorMessage =
            "This release is no longer available. It may have been deleted or archived.";
        } else {
          // Generic error
          this.errorMessage =
            serverMessage ||
            "Unable to load release. Please try generating a new install link from the TestApp.io mobile app.";
        }
      }
    },
    installApp() {
      if (!this.release) return;

      this.installing = true;

      // Build itms-services URL with stats tracking
      const manifestUrl = this.release.manifest_url;
      const query = `?user_id=${this.release.user_id || ""}&device_id=${
        this.release.device_id || ""
      }&timestamp=${Date.now()}`;

      // Trigger iOS installation
      window.location.replace(
        `itms-services://?action=download-manifest&url=${encodeURIComponent(
          manifestUrl + query
        )}`
      );

      // Show instructions after a delay
      setTimeout(() => {
        this.installing = false;
        this.showInstructions = true;
      }, 2000);
    },
  },
};
</script>

<style scoped>
.lightBackground {
  background-color: #f6f6f6;
  min-height: 100vh;
}

.darkBackground {
  background-color: #121212;
  min-height: 100vh;
}
</style>
